import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import FileUploader from '../components/FileUploader';
import TopicalMap3DVisualization from '../components/TopicalMap3DVisualization';
import { api } from '../services/api';

interface RecentJob {
  id: number;
  project_id: number;
  status: string;
  completed_at: string | null;
  created_at: string;
  settings: Record<string, any> | null;
}

interface FactsAgentJob {
  id: number;
  created_at: string | null;
  completed_at: string | null;
  settings: Record<string, any> | null;
  output_count: number;
  filename: string;
  word_count?: number;
  label?: string;
}

interface TopicalMapPage {
  id: number;
  job_id: number;
  generator_type: string;
  main_topic: string | null;
  sub_topic: string | null;
  title: string;
  h1: string | null;
  meta_title: string | null;
  meta_description: string | null;
  url: string | null;
  primary_keyword: string | null;
  search_intent: string | null;
  priority_score: number | null;
  status: string;
  generated_at: string;
}

interface Summary {
  total_topics: number;
  internal_links: number;
  duplicates_removed: number;
  categories: Record<string, number>;
  by_priority: Record<string, number>;
  pillar_pages: Array<{ title: string; url: string; category: string }>;
}

// Agent pipeline stages
const AGENT_STAGES = [
  { id: 'factsheet_parser', name: 'Factsheet Parser', description: 'Extracting client data', progress: 15 },
  { id: 'topical_mapper', name: 'Topical Mapper', description: 'Loading topic database', progress: 25 },
  { id: 'selection_engine', name: 'Selection Engine', description: 'Selecting relevant topics', progress: 50 },
  { id: 'merger_agent', name: 'Merger Agent', description: 'Deduplicating topics', progress: 65 },
  { id: 'categorizer_agent', name: 'Categorizer', description: 'Organizing categories', progress: 80 },
  { id: 'linking_agent', name: 'Linking Agent', description: 'Creating internal links', progress: 95 },
];

// Category colors for badges
const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
  core_pages: { bg: 'bg-blue-100', text: 'text-blue-800' },
  program_pages: { bg: 'bg-green-100', text: 'text-green-800' },
  conditions_treated: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  therapies_pages: { bg: 'bg-purple-100', text: 'text-purple-800' },
  outer_section: { bg: 'bg-indigo-100', text: 'text-indigo-800' },
  local_outer_section: { bg: 'bg-rose-100', text: 'text-rose-800' },
  uncategorized: { bg: 'bg-gray-100', text: 'text-gray-800' },
};

type InputMode = 'file' | 'text' | 'factsagent';

export default function TopicalMapPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id');

  const [step, setStep] = useState<'upload' | 'configure' | 'processing' | 'results'>('upload');
  const [jobId, setJobId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Input mode state
  const [inputMode, setInputMode] = useState<InputMode>('factsagent');
  const [textContent, setTextContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // FactsAgent jobs state
  const [factsAgentJobs, setFactsAgentJobs] = useState<FactsAgentJob[]>([]);
  const [selectedFactsAgentJobId, setSelectedFactsAgentJobId] = useState<number | null>(null);
  const [factsAgentContent, setFactsAgentContent] = useState<string | null>(null);

  // Client info state (new)
  const [clientName, setClientName] = useState('');
  const [clientDomain, setClientDomain] = useState('');
  const [existingMapFile, setExistingMapFile] = useState<File | null>(null);
  const [useAnthropic, setUseAnthropic] = useState(true);

  // Recent jobs state
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);

  // Processing state
  const [currentStage, setCurrentStage] = useState<string>('');
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');

  // Results state
  const [results, setResults] = useState<TopicalMapPage[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Selection and calendar state
  const [selectedTopics, setSelectedTopics] = useState<Set<number>>(new Set());
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [calendarDateMode, setCalendarDateMode] = useState<'single' | 'range'>('single');
  const [singleDate, setSingleDate] = useState(new Date().toISOString().split('T')[0]);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [calendarEntries, setCalendarEntries] = useState<Array<{ topic_id: number; scheduled_date: string }>>([]);

  // 3D Visualization state
  const [show3DVisualization, setShow3DVisualization] = useState(false);

  const urlJobId = searchParams.get('job_id');

  // Load calendar entries for this project
  const loadCalendarEntries = useCallback(async () => {
    if (!projectId || !jobId) return;
    try {
      const response = await api.get(`/topicalmap/${jobId}/calendar`);
      setCalendarEntries(response.data.entries || []);
    } catch (err) {
      console.error('Failed to load calendar entries:', err);
    }
  }, [projectId, jobId]);

  // Check if topic is in calendar
  const isInCalendar = (topicId: number) => {
    return calendarEntries.some(entry => entry.topic_id === topicId);
  };

  // Toggle topic selection
  const toggleTopicSelection = (topicId: number) => {
    const newSelected = new Set(selectedTopics);
    if (newSelected.has(topicId)) {
      newSelected.delete(topicId);
    } else {
      newSelected.add(topicId);
    }
    setSelectedTopics(newSelected);
  };

  // Select all filtered topics
  const selectAllFiltered = () => {
    const newSelected = new Set(selectedTopics);
    filteredResults.forEach(topic => newSelected.add(topic.id));
    setSelectedTopics(newSelected);
  };

  // Deselect all
  const deselectAll = () => {
    setSelectedTopics(new Set());
  };

  // Add selected topics to calendar
  const addToCalendar = async () => {
    if (selectedTopics.size === 0) {
      setError('Please select at least one topic');
      return;
    }

    const topicsArray = Array.from(selectedTopics);
    const entries = [];

    if (calendarDateMode === 'single') {
      // Add all on the same date
      entries.push(...topicsArray.map(topicId => ({
        topic_id: topicId,
        scheduled_date: singleDate,
      })));
    } else {
      // Spread across date range (skip weekends)
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      let currentDate = new Date(start);
      let topicIndex = 0;

      while (topicIndex < topicsArray.length && currentDate <= end) {
        // Skip weekends
        if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
          entries.push({
            topic_id: topicsArray[topicIndex],
            scheduled_date: currentDate.toISOString().split('T')[0],
          });
          topicIndex++;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    try {
      await api.post(`/topicalmap/${jobId}/calendar`, { entries });
      setShowCalendarModal(false);
      setSelectedTopics(new Set());
      loadCalendarEntries();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add topics to calendar');
    }
  };

  useEffect(() => {
    if (projectId) {
      loadRecentJobs();
      loadFactsAgentJobs();
    }
  }, [projectId]);

  // Auto-load specific job from URL
  useEffect(() => {
    if (urlJobId && projectId) {
      const id = parseInt(urlJobId);
      if (!isNaN(id)) {
        viewJob({ id } as RecentJob);
      }
    }
  }, [urlJobId, projectId]);

  const loadFactsAgentJobs = async () => {
    if (!projectId) return;
    try {
      // Use the factsheets selector endpoint for better data
      const response = await api.get(`/factsheets/project/${projectId}/selector`);
      const factSheets = response.data.fact_sheets || [];
      // Map to our interface
      const jobs: FactsAgentJob[] = factSheets.map((sheet: any) => ({
        id: sheet.job_id,
        created_at: sheet.completed_at,
        completed_at: sheet.completed_at,
        settings: null,
        output_count: 6,
        filename: sheet.label || `Job #${sheet.job_id}`,
        word_count: sheet.word_count,
        label: sheet.label
      }));
      setFactsAgentJobs(jobs);
      // Auto-select the most recent job if available
      if (jobs.length > 0) {
        setSelectedFactsAgentJobId(jobs[0].id);
      }
    } catch (err) {
      console.error('Failed to load FactsAgent jobs:', err);
    }
  };

  const loadFactsAgentContent = async (jobId: number) => {
    try {
      // Use the factsheets content endpoint for consolidated facts
      const response = await api.get(`/factsheets/${jobId}/content`);
      setFactsAgentContent(response.data.content);
    } catch (err) {
      console.error('Failed to load FactsAgent content:', err);
      setFactsAgentContent(null);
    }
  };

  useEffect(() => {
    if (selectedFactsAgentJobId) {
      loadFactsAgentContent(selectedFactsAgentJobId);
    }
  }, [selectedFactsAgentJobId]);

  const loadRecentJobs = async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/projects/${projectId}/jobs?system_type=topicalmap&status=completed`);
      setRecentJobs(response.data || []);
    } catch (err) {
      console.error('Failed to load recent jobs:', err);
    }
  };

  const viewJob = async (job: RecentJob) => {
    setJobId(job.id);
    await loadResults(job.id);
    setStep('results');
  };

  const loadResults = async (jId: number) => {
    try {
      const [resultsRes, summaryRes] = await Promise.all([
        api.get(`/topicalmap/results/${jId}`),
        api.get(`/topicalmap/summary/${jId}`)
      ]);
      setResults(resultsRes.data.pages || []);
      setSummary(summaryRes.data);
      loadCalendarEntries(); // Load calendar entries when results load
    } catch (err) {
      console.error('Failed to load results:', err);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!projectId) {
      setError('No project ID provided. Please select a project first.');
      return;
    }

    try {
      setError(null);
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_id', projectId);

      const response = await api.post('/topicalmap/upload', formData);
      // Extract job_id from response message or data
      const jobIdMatch = response.data.message?.match(/Job ID: (\d+)/);
      const newJobId = jobIdMatch ? parseInt(jobIdMatch[1]) : response.data.job_id;
      setJobId(newJobId);
      setStep('configure');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTextSubmit = async () => {
    if (!projectId) {
      setError('No project ID provided. Please select a project first.');
      return;
    }

    if (!textContent.trim()) {
      setError('Please enter the about content.');
      return;
    }

    try {
      setError(null);
      setIsSubmitting(true);

      // Create a file from the text content
      const blob = new Blob([textContent], { type: 'text/plain' });
      const file = new File([blob], 'about.txt', { type: 'text/plain' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_id', projectId);

      const response = await api.post('/topicalmap/upload', formData);
      const jobIdMatch = response.data.message?.match(/Job ID: (\d+)/);
      const newJobId = jobIdMatch ? parseInt(jobIdMatch[1]) : response.data.job_id;
      setJobId(newJobId);
      setStep('configure');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit content');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExistingMapUpload = async (file: File) => {
    if (!jobId) return;

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('job_id', jobId.toString());

      await api.post('/topicalmap/upload-existing-map', formData);
      setExistingMapFile(file);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload existing map');
    }
  };

  const handleFactsAgentSelect = async () => {
    if (!projectId || !selectedFactsAgentJobId) {
      setError('Please select a FactsAgent job.');
      return;
    }

    try {
      setError(null);
      setIsSubmitting(true);
      // Move directly to configure step - job will be created when generating
      setStep('configure');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to select FactsAgent job');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerate = async () => {
    try {
      setError(null);

      if (inputMode === 'factsagent' && selectedFactsAgentJobId) {
        // Generate from FactsAgent job
        const response = await api.post(
          `/topicalmap/generate-from-factsagent/${selectedFactsAgentJobId}?project_id=${projectId}`,
          {
            client_name: clientName || 'Client',
            client_domain: clientDomain || undefined,
            use_anthropic: useAnthropic
          }
        );
        setJobId(response.data.id);
        setStep('processing');
        startPollingForJob(response.data.id);
      } else if (jobId) {
        // Generate from uploaded file
        await api.post(`/topicalmap/v2/generate/${jobId}`, {
          client_name: clientName || 'Client',
          client_domain: clientDomain || undefined,
          use_anthropic: useAnthropic
        });
        setStep('processing');
        startPolling();
      } else {
        setError('No input source selected');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start generation');
    }
  };

  const startPollingForJob = useCallback((jId: number) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/topicalmap/status/${jId}`);
        const status = response.data;

        setProgress(status.progress || 0);
        setStatusMessage(status.recent_logs?.[0] || 'Processing...');

        // Determine current stage based on progress
        for (const stage of AGENT_STAGES) {
          if (status.progress <= stage.progress) {
            setCurrentStage(stage.id);
            break;
          }
        }

        if (status.status === 'completed') {
          clearInterval(pollInterval);
          await loadResults(jId);
          setStep('results');
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          setError(status.error_message || 'Generation failed');
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, []);

  const startPolling = useCallback(() => {
    if (!jobId) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/topicalmap/status/${jobId}`);
        const status = response.data;

        setProgress(status.progress || 0);
        setStatusMessage(status.recent_logs?.[0] || 'Processing...');

        // Determine current stage based on progress
        for (const stage of AGENT_STAGES) {
          if (status.progress <= stage.progress) {
            setCurrentStage(stage.id);
            break;
          }
        }

        if (status.status === 'completed') {
          clearInterval(pollInterval);
          await loadResults(jobId);
          setStep('results');
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          setError(status.error_message || 'Generation failed');
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [jobId]);

  const handleReset = () => {
    setStep('upload');
    setJobId(null);
    setError(null);
    setTextContent('');
    setInputMode('factsagent');
    setClientName('');
    setClientDomain('');
    setExistingMapFile(null);
    setResults([]);
    setSummary(null);
    setCategoryFilter([]);
    setPriorityFilter([]);
    setSearchQuery('');
    setSelectedFactsAgentJobId(factsAgentJobs[0]?.id || null);
    setFactsAgentContent(null);
  };

  // Filter results
  const filteredResults = results.filter(page => {
    if (categoryFilter.length > 0 && !categoryFilter.includes(page.generator_type)) return false;

    if (priorityFilter.length > 0) {
      const score = page.priority_score || 0;
      const priority = score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low';
      if (!priorityFilter.includes(priority)) return false;
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        page.title.toLowerCase().includes(query) ||
        page.primary_keyword?.toLowerCase().includes(query) ||
        page.url?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  // Get unique categories from results
  const categories = [...new Set(results.map(p => p.generator_type))];

  return (
    <Layout projectId={projectId ? parseInt(projectId) : null}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="page-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">TopicalMap Generator</h1>
              <p className="page-subtitle">Multi-Agent SEO Topical Map Generation System
                <a href="https://drive.google.com/file/d/1JD_SnqkoBQFAtxgqg34Uglzam3zVmObo/view?usp=sharing" target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" /></svg>
                  Video Tutorial
                </a>
              </p>
            </div>
            {step === 'results' && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => window.open(`/api/topicalmap/download/${jobId}?token=${localStorage.getItem('auth_token') || ''}`, '_blank')}
                  className="btn-secondary"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  CSV
                </button>
                <button
                  onClick={() => window.open(`/api/topicalmap/download/${jobId}/json?token=${localStorage.getItem('auth_token') || ''}`, '_blank')}
                  className="btn-secondary"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  JSON
                </button>
                <button onClick={handleReset} className="btn-primary">
                  New Map
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-danger-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-danger-700">{error}</span>
          </div>
        )}

        {/* Recent Jobs Section */}
        {recentJobs.length > 0 && step === 'upload' && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Previous Topical Maps ({recentJobs.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentJobs.map((job) => {
                const clientName = (job.settings as any)?.client_name || 'Client';
                return (
                  <div
                    key={job.id}
                    onClick={() => viewJob(job)}
                    className="card hover:shadow-lg hover:border-brand-200 transition-all cursor-pointer group"
                  >
                    <div className="card-body">
                      <div className="flex items-start space-x-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 group-hover:text-brand-600 transition-colors truncate">
                            {clientName}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1 flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {new Date(job.completed_at || job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                          {(job.settings as any)?.pipeline_version === 'v2' && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-brand-100 text-brand-700 rounded-full">
                              Multi-Agent
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <span className="text-brand-600 text-sm font-medium group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                          View Results
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">Or create a new topical map below</p>
            </div>
          </div>
        )}

        {/* Upload Step */}
        {step === 'upload' && (
          <div className="max-w-2xl mx-auto">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Client Fact Sheet</h2>
                    <p className="text-gray-500">Upload a file or paste your client information</p>
                  </div>
                </div>

                {/* Input Mode Toggle */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1 mb-6">
                  {factsAgentJobs.length > 0 && (
                    <button
                      onClick={() => setInputMode('factsagent')}
                      className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                        inputMode === 'factsagent'
                          ? 'bg-white shadow-sm text-gray-900'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      From FactsAgent
                    </button>
                  )}
                  <button
                    onClick={() => setInputMode('file')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      inputMode === 'file'
                        ? 'bg-white shadow-sm text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload File
                  </button>
                  <button
                    onClick={() => setInputMode('text')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                      inputMode === 'text'
                        ? 'bg-white shadow-sm text-gray-900'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Paste Text
                  </button>
                </div>

                {/* FactsAgent Selection Mode */}
                {inputMode === 'factsagent' && (
                  <div className="space-y-4">
                    {factsAgentJobs.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="font-medium">No FactsAgent outputs found</p>
                        <p className="text-sm mt-1">Run FactsAgent first to generate facts for this project.</p>
                      </div>
                    ) : (
                      <>
                        {/* Fact Sheets Selector Cards */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Select Consolidated Facts Source
                          </label>
                          <div className="space-y-2">
                            {factsAgentJobs.map((job) => (
                              <div
                                key={job.id}
                                onClick={() => setSelectedFactsAgentJobId(job.id)}
                                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                  selectedFactsAgentJobId === job.id
                                    ? 'border-brand-500 bg-brand-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                      selectedFactsAgentJobId === job.id
                                        ? 'border-brand-500 bg-brand-500'
                                        : 'border-gray-300'
                                    }`}>
                                      {selectedFactsAgentJobId === job.id && (
                                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">{job.label || job.filename}</p>
                                      <p className="text-sm text-gray-500">
                                        Job #{job.id}
                                        {job.word_count && ` • ${job.word_count.toLocaleString()} words`}
                                      </p>
                                    </div>
                                  </div>
                                  {job.completed_at && (
                                    <span className="text-xs text-gray-400">
                                      {new Date(job.completed_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Consolidated Facts Content Preview */}
                        {selectedFactsAgentJobId && factsAgentContent && (
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            <div className="bg-green-50 px-4 py-3 border-b border-green-200 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-sm font-semibold text-green-800">Consolidated Final Facts</span>
                              </div>
                              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                {factsAgentContent.split(/\s+/).length.toLocaleString()} words
                              </span>
                            </div>
                            <div className="p-4 max-h-80 overflow-y-auto bg-white">
                              <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                                {factsAgentContent}
                              </pre>
                            </div>
                          </div>
                        )}

                        {selectedFactsAgentJobId && !factsAgentContent && (
                          <div className="flex items-center justify-center py-8 text-gray-500">
                            <span className="spinner mr-2"></span>
                            <span>Loading consolidated facts...</span>
                          </div>
                        )}

                        <button
                          onClick={handleFactsAgentSelect}
                          disabled={isSubmitting || !selectedFactsAgentJobId}
                          className="btn-primary w-full"
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner"></span>
                              Processing...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                              Continue to Configuration
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* File Upload Mode */}
                {inputMode === 'file' && (
                  <FileUploader
                    onFileSelect={handleFileUpload}
                    accept=".txt"
                    maxSize={5 * 1024 * 1024}
                    disabled={isSubmitting}
                  />
                )}

                {/* Text Input Mode */}
                {inputMode === 'text' && (
                  <div>
                    <textarea
                      value={textContent}
                      onChange={(e) => setTextContent(e.target.value)}
                      placeholder="Paste your client fact sheet content here...

Include:
- Business name and location
- Services offered
- Treatment programs
- Insurance accepted
- Unique selling points"
                      className="textarea h-64 font-mono text-sm"
                      disabled={isSubmitting}
                    />
                    <button
                      onClick={handleTextSubmit}
                      disabled={isSubmitting || !textContent.trim()}
                      className="btn-primary w-full mt-4"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                          Continue to Configuration
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Info Card */}
            <div className="mt-6 card bg-brand-50 border-brand-200">
              <div className="card-body">
                <h3 className="font-semibold text-brand-900 mb-2">
                  {inputMode === 'factsagent' ? 'Using FactsAgent Output' : 'Fact Sheet Guidelines'}
                </h3>
                {inputMode === 'factsagent' ? (
                  <ul className="text-sm text-brand-700 space-y-1.5 list-disc list-inside">
                    <li>Select a completed FactsAgent job from this project</li>
                    <li>The consolidated facts will be used for topic selection</li>
                    <li>Ensures consistency with other generated content</li>
                    <li>Recommended workflow for best results</li>
                  </ul>
                ) : (
                  <ul className="text-sm text-brand-700 space-y-1.5 list-disc list-inside">
                    <li>Include business name, location, and services</li>
                    <li>Add treatment programs and specializations</li>
                    <li>Describe your unique value propositions</li>
                    <li>Include insurance and payment information</li>
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Configure Step */}
        {step === 'configure' && (
          <div className="max-w-3xl mx-auto">
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900">Configure Generation</h2>
                <p className="text-sm text-gray-500 mt-1">Set client details and options for the multi-agent pipeline</p>
              </div>
              <div className="card-body space-y-6">
                {/* Source Information */}
                {inputMode === 'factsagent' && selectedFactsAgentJobId && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-green-900">Using FactsAgent Output</p>
                        <p className="text-sm text-green-700">
                          Job #{selectedFactsAgentJobId} - {factsAgentJobs.find(j => j.id === selectedFactsAgentJobId)?.filename}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Client Information */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-4">Client Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client Name
                      </label>
                      <input
                        type="text"
                        value={clientName}
                        onChange={(e) => setClientName(e.target.value)}
                        placeholder="e.g., Charlotte Detox"
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Domain (optional)
                      </label>
                      <input
                        type="text"
                        value={clientDomain}
                        onChange={(e) => setClientDomain(e.target.value)}
                        placeholder="e.g., charlottedetox.com"
                        className="input"
                      />
                    </div>
                  </div>
                </div>

                {/* Existing Map Upload */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Existing Topical Map (Optional)</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Upload an existing topical map to avoid duplicates during generation
                  </p>
                  {existingMapFile ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-green-800 font-medium">{existingMapFile.name}</span>
                      <button
                        onClick={() => setExistingMapFile(null)}
                        className="ml-auto text-green-600 hover:text-green-800"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <FileUploader
                      onFileSelect={handleExistingMapUpload}
                      accept=".csv"
                      maxSize={10 * 1024 * 1024}
                    />
                  )}
                </div>

                {/* API Provider Toggle */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">AI Provider</h3>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={useAnthropic}
                        onChange={() => setUseAnthropic(true)}
                        className="text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-sm text-gray-700">Claude (Anthropic)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!useAnthropic}
                        onChange={() => setUseAnthropic(false)}
                        className="text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-sm text-gray-700">GPT-4 (OpenAI)</span>
                    </label>
                  </div>
                </div>

                {/* Agent Pipeline Preview */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-4">6-Agent Pipeline</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {AGENT_STAGES.map((stage, index) => (
                      <div key={stage.id} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <span className="w-6 h-6 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{stage.name}</p>
                          <p className="text-xs text-gray-500">{stage.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="card-footer flex justify-between">
                <button onClick={() => setStep('upload')} className="btn-secondary">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
                <button onClick={handleGenerate} className="btn-primary">
                  Generate Topical Map
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Processing Step */}
        {step === 'processing' && jobId && (
          <div className="max-w-4xl mx-auto">
            <div className="card">
              <div className="card-body">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Generating Topical Map</h2>
                  <p className="text-gray-500">Running 6-agent pipeline. This may take several minutes.</p>
                </div>

                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">{statusMessage || 'Initializing...'}</span>
                    <span className="font-medium text-brand-600">{progress}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-brand-500 to-brand-600 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                {/* Agent Pipeline Visualization */}
                <div className="space-y-3">
                  {AGENT_STAGES.map((stage, index) => {
                    const isComplete = progress > stage.progress;
                    const isCurrent = currentStage === stage.id;

                    return (
                      <div
                        key={stage.id}
                        className={`flex items-center gap-4 p-4 rounded-lg transition-all ${
                          isComplete ? 'bg-green-50 border border-green-200' :
                          isCurrent ? 'bg-brand-50 border border-brand-200' :
                          'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isComplete ? 'bg-green-500 text-white' :
                          isCurrent ? 'bg-brand-500 text-white animate-pulse' :
                          'bg-gray-300 text-gray-600'
                        }`}>
                          {isComplete ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <span className="text-sm font-medium">{index + 1}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${isComplete ? 'text-green-900' : isCurrent ? 'text-brand-900' : 'text-gray-500'}`}>
                            {stage.name}
                          </p>
                          <p className={`text-sm ${isComplete ? 'text-green-700' : isCurrent ? 'text-brand-700' : 'text-gray-400'}`}>
                            {stage.description}
                          </p>
                        </div>
                        {isCurrent && <span className="spinner text-brand-600"></span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Step */}
        {step === 'results' && jobId && (
          <div className="space-y-6">
            {/* Action Bar */}
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Topical Map Results
              </h2>
              <button
                onClick={() => setShow3DVisualization(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                </svg>
                View 3D Visualization
              </button>
            </div>

            {/* Summary Metrics */}
            {summary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="card">
                  <div className="card-body text-center">
                    <p className="text-3xl font-bold text-gray-900">{summary.total_topics}</p>
                    <p className="text-sm text-gray-500">Total Topics</p>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body text-center">
                    <p className="text-3xl font-bold text-green-600">{summary.by_priority?.high || 0}</p>
                    <p className="text-sm text-gray-500">High Priority</p>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body text-center">
                    <p className="text-3xl font-bold text-yellow-600">{summary.by_priority?.medium || 0}</p>
                    <p className="text-sm text-gray-500">Medium Priority</p>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body text-center">
                    <p className="text-3xl font-bold text-gray-600">{summary.by_priority?.low || 0}</p>
                    <p className="text-sm text-gray-500">Low Priority</p>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body text-center">
                    <p className="text-3xl font-bold text-purple-600">{summary.duplicates_removed}</p>
                    <p className="text-sm text-gray-500">Duplicates Removed</p>
                  </div>
                </div>
              </div>
            )}

            {/* Category Distribution */}
            {summary && Object.keys(summary.categories).length > 0 && (
              <div className="card">
                <div className="card-header">
                  <h3 className="font-semibold text-gray-900">Category Distribution</h3>
                </div>
                <div className="card-body">
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(summary.categories).map(([cat, count]) => {
                      const colors = CATEGORY_COLORS[cat] || CATEGORY_COLORS.uncategorized;
                      return (
                        <div
                          key={cat}
                          className={`px-4 py-2 rounded-lg ${colors.bg}`}
                        >
                          <span className={`text-2xl font-bold ${colors.text}`}>{count}</span>
                          <span className={`ml-2 text-sm ${colors.text}`}>
                            {cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Results Table */}
            <div className="card">
              <div className="card-header">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <h3 className="font-semibold text-gray-900">Generated Topics ({filteredResults.length})</h3>
                    {selectedTopics.size > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">({selectedTopics.size} selected)</span>
                        <button
                          onClick={() => setShowCalendarModal(true)}
                          className="btn btn-primary btn-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Add to Calendar
                        </button>
                        <button
                          onClick={deselectAll}
                          className="btn btn-secondary btn-sm"
                        >
                          Clear Selection
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {/* Category Filter */}
                    <select
                      value={categoryFilter[0] || ''}
                      onChange={(e) => setCategoryFilter(e.target.value ? [e.target.value] : [])}
                      className="input py-1.5 text-sm"
                    >
                      <option value="">All Categories</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>
                          {cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>

                    {/* Priority Filter */}
                    <select
                      value={priorityFilter[0] || ''}
                      onChange={(e) => setPriorityFilter(e.target.value ? [e.target.value] : [])}
                      className="input py-1.5 text-sm"
                    >
                      <option value="">All Priorities</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>

                    {/* Search */}
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search topics..."
                      className="input py-1.5 text-sm w-48"
                    />
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-center w-12">
                        <input
                          type="checkbox"
                          checked={filteredResults.length > 0 && filteredResults.every(t => selectedTopics.has(t.id))}
                          onChange={(e) => e.target.checked ? selectAllFiltered() : deselectAll()}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keyword</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredResults.slice(0, 100).map((page) => {
                      const colors = CATEGORY_COLORS[page.generator_type] || CATEGORY_COLORS.uncategorized;
                      const score = page.priority_score || 0;
                      const priority = score >= 70 ? 'High' : score >= 40 ? 'Medium' : 'Low';
                      const priorityColor = score >= 70 ? 'text-green-600' : score >= 40 ? 'text-yellow-600' : 'text-gray-500';

                      const inCalendar = isInCalendar(page.id);

                      return (
                        <tr key={page.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedTopics.has(page.id)}
                              onChange={() => toggleTopicSelection(page.id)}
                              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                              {page.generator_type.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-900">{page.title}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-500 font-mono">{page.url || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-700">{page.primary_keyword || '-'}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm font-medium ${priorityColor}`}>
                              {priority} ({score})
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {inCalendar && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                In Calendar
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredResults.length > 100 && (
                  <div className="p-4 text-center text-sm text-gray-500 bg-gray-50">
                    Showing first 100 of {filteredResults.length} results. Download CSV/JSON for full data.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add to Calendar Modal */}
      {showCalendarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Topics to Calendar</h3>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Adding {selectedTopics.size} topic{selectedTopics.size !== 1 ? 's' : ''} to calendar
              </p>
            </div>

            {/* Date Mode Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Mode</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={calendarDateMode === 'single'}
                    onChange={() => setCalendarDateMode('single')}
                    className="mr-2"
                  />
                  <span className="text-sm">Same Date</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    checked={calendarDateMode === 'range'}
                    onChange={() => setCalendarDateMode('range')}
                    className="mr-2"
                  />
                  <span className="text-sm">Date Range</span>
                </label>
              </div>
            </div>

            {/* Date Inputs */}
            {calendarDateMode === 'single' ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Date</label>
                <input
                  type="date"
                  value={singleDate}
                  onChange={(e) => setSingleDate(e.target.value)}
                  className="input w-full"
                />
              </div>
            ) : (
              <div className="mb-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    className="input w-full"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Topics will be spread across this date range (weekends skipped)
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCalendarModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={addToCalendar}
                className="btn btn-primary"
              >
                Add to Calendar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3D Visualization Modal */}
      {show3DVisualization && jobId && (
        <TopicalMap3DVisualization
          jobId={jobId}
          onClose={() => setShow3DVisualization(false)}
        />
      )}
    </Layout>
  );
}
